/**
 * @file agencyContext.js
 * @description Team logins, client side. Who the signed-in user is to an agency (owner,
 *   member, nobody) and every team action, in one place.
 *
 *   Mirrors the server engine (supabase/migrations/20261105090000 + 20261105100000):
 *   acting_agency_id() / _acting_agency_for() decide which agency a user works for, and
 *   every policy and RPC enforces it. This module only drives what the app shows: the
 *   agency id passed to agency screens, owner-only screens hidden for members, and the
 *   Team section. The server is always the enforcement.
 *
 *   A member works as the agency only while active AND the agency is Pro; otherwise the
 *   server reports status "paused" (agency not Pro) or "removed".
 * @module lib/agencyContext
 */
import { supabase } from "./supabase";
import { logCaughtError } from "./errorService";
import { functionErrorMessage } from "./functionErrorMessage";
import { recordBothAcceptances } from "./legalAcceptanceService";

/** @typedef {{ role: "owner"|"member"|"none", agencyId: string|null, status: "active"|"paused"|"removed"|null,
 *   agencyName: string|null, ownerName: string|null, memberId: string|null,
 *   seatLimit: number|null, seatsUsed: number|null, isPro: boolean }} AgencyContext */

/** @returns {AgencyContext} */
export function emptyAgencyContext() {
  return { role: "none", agencyId: null, status: null, agencyName: null, ownerName: null, memberId: null, seatLimit: null, seatsUsed: null, isPro: false };
}

/** Shapes the agency_team_context() payload. Pure, exported for tests. */
export function toAgencyContext(raw) {
  if (!raw || (raw.role !== "owner" && raw.role !== "member")) return emptyAgencyContext();
  return {
    role: raw.role,
    agencyId: raw.agency_id || null,
    status: raw.status || null,
    agencyName: raw.agency_name || null,
    ownerName: raw.owner_name || null,
    memberId: raw.member_id || null,
    seatLimit: raw.seat_limit ?? null,
    seatsUsed: raw.seats_used ?? null,
    // Owner only (server rule agency_is_pro): drives the PRO tag on the Team menu item.
    isPro: raw.is_pro === true,
  };
}

/** @returns {Promise<AgencyContext>} */
export async function loadAgencyContext() {
  const { data, error } = await supabase.rpc("agency_team_context");
  if (error) {
    logCaughtError(error, "team_load_agency_context_failed");
    return emptyAgencyContext();
  }
  return toAgencyContext(data);
}

export const isTeamMember = (ctx) => ctx?.role === "member";

/** Screens only the agency admin uses (account menu items and their pages). Teammates are not sent there. */
export const OWNER_ONLY_VIEWS = new Set(["agencyabout", "team", "billing"]);
export const isAgencyOwner = (ctx) => ctx?.role === "owner";
/** A member who can work right now (not paused, not removed). */
export const isActiveMember = (ctx) => ctx?.role === "member" && ctx.status === "active";

/**
 * The agency id agency screens should use. A member works on the owner's agency; everyone
 * else (owners, and members whose access is paused or removed) on their own id, which for a
 * paused or removed member simply shows nothing, exactly like the server.
 */
export function actingAgencyId(ctx, userId) {
  return isActiveMember(ctx) ? ctx.agencyId : userId;
}

/** Owner-only screens and actions (plan, billing, buying credits, agency profile, NPI, team). */
export function canManageAgency(ctx) {
  return !isTeamMember(ctx);
}

/** "Maria Rojas" -> "Maria R.", for attribution labels. */
export function shortPersonName(full) {
  const parts = String(full || "").trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "";
  if (parts.length === 1) return parts[0];
  return `${parts[0]} ${parts[parts.length - 1][0].toUpperCase()}.`;
}

// ── Team section ────────────────────────────────────────────────────────────

/** Error codes raised by the team RPCs and the team-invite edge function, as user copy keys. */
export const TEAM_ERRORS = {
  team_requires_pro:      "Team logins are part of Pro.",
  team_owner_only:        "Only the agency owner can manage the team.",
  team_invalid_email:     "Enter a valid email address.",
  team_email_has_account: "This email already has an ABA Cases account. Invite a different email.",
  team_already_invited:   "This email is already on your team or has a pending invite.",
  team_no_seats:          "All logins in your plan are in use. Remove someone or cancel an invite first.",
  team_invite_not_found:  "That invite is no longer pending.",
  team_member_not_found:  "That person is no longer on your team.",
  team_resend_limit:      "This invite reached its resend limit for the last 24 hours. Try again later.",
  team_member_unavailable: "This person is already on a team again, so they cannot be restored here.",
  team_email_failed:      "The invite was saved but the email did not send. Use Resend to try again.",
};

/** Maps any error (RPC, edge function, network) to a short message. Pure. */
export function teamErrorMessage(err) {
  const text = String(err?.code || err?.error || err?.message || err || "");
  // The team-invite edge function returns the codes without the "team_" prefix.
  const code = Object.keys(TEAM_ERRORS).find((k) => text.includes(k) || text.includes(k.replace(/^team_/, "")));
  return code ? TEAM_ERRORS[code] : "Something went wrong. Please try again.";
}

/**
 * @returns {Promise<{ isOwner: boolean, isPro: boolean, seatLimit: number, seatsUsed: number,
 *   members: Array<{ memberId: string|null, userId: string|null, role: "owner"|"member",
 *   status: "active"|"invited"|"expired", name: string|null, email: string|null,
 *   inviteSentAt: string|null, inviteExpiresAt: string|null, acceptedAt: string|null,
 *   lastActiveAt: string|null, resendsToday: number, nextResendAt: string|null (set while Resend is capped),
 *   isYou: boolean }>, resendLimit: number (team_invite_resends_per_day),
 *   past: Array<{ memberId: string, name: string|null, email: string|null, acceptedAt: string|null,
 *   removedAt: string|null, lastActiveAt: string|null }> }>}  past = revoked teammates (owner only), for Restore.
 */
export async function loadTeam() {
  const { data, error } = await supabase.rpc("agency_team_list");
  if (error) throw error;
  return {
    isOwner: !!data?.is_owner,
    isPro: !!data?.is_pro,
    seatLimit: data?.seat_limit ?? 1,
    seatsUsed: data?.seats_used ?? 1,
    resendLimit: data?.resend_limit ?? 2,
    members: (data?.members || []).map((m) => ({
      memberId: m.member_id || null,
      userId: m.user_id || null,
      role: m.role,
      status: m.status,
      name: m.name || null,
      email: m.email || null,
      inviteSentAt: m.invite_sent_at || null,
      inviteExpiresAt: m.invite_expires_at || null,
      acceptedAt: m.accepted_at || null,
      lastActiveAt: m.last_active_at || null,
      resendsToday: m.resends_today ?? 0,
      nextResendAt: m.next_resend_at || null,
      isYou: !!m.is_you,
    })),
    past: (data?.past || []).map((m) => ({
      memberId: m.member_id,
      name: m.name || null,
      email: m.email || null,
      acceptedAt: m.accepted_at || null,
      removedAt: m.removed_at || null,
      lastActiveAt: m.last_active_at || null,
    })),
  };
}

async function callTeamInvite(body) {
  const { data, error } = await supabase.functions.invoke("team-invite", { body });
  if (error) {
    // supabase-js wraps non-2xx responses; the JSON body carries our error code.
    throw new Error(await functionErrorMessage(error));
  }
  if (data?.error) throw new Error(data.error);
  return data;
}

/** Sends a team invite email. Owner only, enforced by the server. */
export function inviteTeammate(email) {
  return callTeamInvite({ action: "invite", email });
}

/** Sends a fresh link for a pending invite (the old link stops working). */
export function resendTeamInvite(memberId) {
  return callTeamInvite({ action: "resend", member_id: memberId });
}

/** Cancels a pending invite or removes a member. Their access ends on their next request. */
export async function removeTeammate(memberId) {
  const { error } = await supabase.rpc("team_member_remove", { p_member_id: memberId });
  if (error) throw error;
}

/** Gives a revoked teammate their access back (owner, Pro, needs a free seat; enforced by the server). */
export async function restoreTeammate(memberId) {
  const { error } = await supabase.rpc("team_member_restore", { p_member_id: memberId });
  if (error) throw error;
}

/** What the join page shows before signup: { status: valid|expired|used|cancelled|invalid, agencyName, email, hasAccount }. */
export async function lookupTeamInvite(token) {
  const { data, error } = await supabase.rpc("team_invite_lookup", { p_token: token });
  if (error) {
    logCaughtError(error, "team_lookup_team_invite_failed");
    return { status: "invalid" };
  }
  return {
    status: data?.status || "invalid",
    agencyName: data?.agency_name || null,
    email: data?.email || null,
    hasAccount: !!data?.has_account,
  };
}

// ── Join page (/team-join?token=...) ────────────────────────────────────────

/** Route the team invite email links to. Rendered by App.js off the pathname, ahead of every gate. */
export const TEAM_JOIN_PATH = "/team-join";

export function isTeamJoinPath(pathname) {
  return String(pathname || "").replace(/\/+$/, "") === TEAM_JOIN_PATH;
}

export function readTeamJoinToken(search) {
  try { return new URLSearchParams(search || "").get("token") || ""; } catch { return ""; }
}

const JOIN_TOKEN_KEY = "aba:team_join_token";

/**
 * The invite token for the join page. Read once from the link, kept for this browser tab (so a reload, or
 * "sign out first, then continue", still has it) and removed from the address bar so it does not linger in
 * history. Cleared by clearTeamJoinToken() once the login is created.
 */
export function takeTeamJoinToken() {
  if (typeof window === "undefined") return "";
  const fromUrl = readTeamJoinToken(window.location.search);
  if (fromUrl) {
    try { sessionStorage.setItem(JOIN_TOKEN_KEY, fromUrl); } catch { /* ignore */ }
    try { window.history.replaceState(window.history.state, "", window.location.pathname); } catch { /* ignore */ }
    return fromUrl;
  }
  try { return sessionStorage.getItem(JOIN_TOKEN_KEY) || ""; } catch { return ""; }
}

export function clearTeamJoinToken() {
  try { sessionStorage.removeItem(JOIN_TOKEN_KEY); } catch { /* ignore */ }
}

/**
 * Creates the teammate's login. The database trigger (handle_new_user) checks the token,
 * activates the membership and creates the profile in the same step; a bad or expired
 * token makes Supabase refuse the signup. The sign-up itself is the terms acceptance,
 * recorded right after like the normal signup does.
 * @returns {Promise<{ ok: true } | { ok: false, message?: string, code?: "invalid" }>}
 */
export async function signUpTeamMember({ token, email, password, fullName }) {
  try { sessionStorage.setItem("aba:signup_in_progress", "1"); } catch { /* ignore */ }
  const { data, error } = await supabase.auth.signUp({
    email: String(email || "").trim(),
    password,
    options: { data: { team_invite_token: token, full_name: String(fullName || "").trim() } },
  });
  if (error) {
    try { sessionStorage.removeItem("aba:signup_in_progress"); } catch { /* ignore */ }
    // The trigger's refusal reaches the client as a generic database error.
    const generic = /database error/i.test(error.message || "");
    if (!generic) return { ok: false, message: error.message };
    return { ok: false, code: "invalid" };
  }
  if (data?.user?.id) {
    await recordBothAcceptances(data.user.id).catch(() => {});
  }
  return { ok: true };
}

// ── Attribution ("by Maria R.") ─────────────────────────────────────────────

/**
 * Names of everyone who was ever on the caller's agency team, for "by Maria R." labels.
 * Removed teammates keep their name on what they did.
 * @returns {Promise<Map<string, { name: string, isOwner: boolean }>>}
 */
export async function loadTeamNames() {
  const { data, error } = await supabase.rpc("agency_team_names");
  if (error) {
    logCaughtError(error, "team_load_team_names_failed");
    return new Map();
  }
  return new Map((data || []).map((r) => [r.user_id, { name: r.full_name || "", isOwner: !!r.is_owner }]));
}

/**
 * Label for an action done by userId, or "" when there is nothing useful to say.
 * Only shown when the agency has more than one person, so solo agencies see no change.
 */
export function attributionLabel(names, userId) {
  if (!userId || !names || names.size < 2) return "";
  const n = names.get(userId);
  return n?.name ? shortPersonName(n.name) : "";
}

/** Who wrote each agency message: Map(messageId -> userId). Agency side only (RLS). */
export async function loadMessageAuthors(messageIds) {
  const ids = (messageIds || []).filter(Boolean);
  if (!ids.length) return new Map();
  const { data, error } = await supabase.from("message_authors").select("message_id, user_id").in("message_id", ids.slice(0, 500));
  if (error) {
    logCaughtError(error, "team_load_message_authors_failed");
    return new Map();
  }
  return new Map((data || []).map((r) => [r.message_id, r.user_id]));
}

// ── Admin ───────────────────────────────────────────────────────────────────

/**
 * Ids of every team member account (any status). Admin lists and counts that walk
 * profiles.role = 'agency' must skip these: a member is not an agency.
 * @returns {Promise<Set<string>>}
 */
export async function loadTeamMemberIds() {
  const { data, error } = await supabase.from("agency_members").select("user_id").not("user_id", "is", null);
  if (error) {
    logCaughtError(error, "team_load_team_member_ids_failed");
    return new Set();
  }
  return new Set((data || []).map((r) => r.user_id));
}
